import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Droplet, UserRound } from 'lucide-react';
import { get, post, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateOnly } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Field, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { PatientPicker } from '../patients/PatientPicker';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { GROUP_LABEL, componentLabel, type BloodUnit } from './BloodBankPage';

const CROSSMATCH_TAB = '/blood-bank?tab=crossmatch';

export function CrossMatchRequestPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), CROSSMATCH_TAB);
  const initialPatientId = params.get('patientId') ?? '';
  const [patientId, setPatientId] = useState(initialPatientId);
  const [component, setComponent] = useState('');
  const [bloodUnitId, setBloodUnitId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const compatible = useQuery({
    queryKey: ['compatible-units', patientId, component],
    queryFn: () => get<BloodUnit[]>(`/blood-bank/patients/${patientId}/compatible-units${qs({ component: component || undefined })}`),
    enabled: Boolean(patientId),
    retry: false,
  });

  const request = useMutation({
    mutationFn: () => post('/blood-bank/cross-matches', { patientId, bloodUnitId }),
    onSuccess: () => {
      toast.success('Cross-match requested', 'The unit is reserved until the result is recorded');
      void queryClient.invalidateQueries({ queryKey: ['cross-matches'] });
      void queryClient.invalidateQueries({ queryKey: ['blood-stock'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const valid = Boolean(patientId) && Boolean(bloodUnitId);
  const dirty = !request.isSuccess && (patientId !== initialPatientId || component !== '' || bloodUnitId !== '');

  return (
    <FormScreen
      title="Request a cross-match"
      description="Only units this patient's group can receive are offered"
      icon={Droplet}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) return;
        setError(null);
        request.mutate();
      }}
    >
      <FormSection title={tr('opd', 'patient')} icon={UserRound} columns={1}>
        <Field label="Patient" required>
          <PatientPicker value={patientId} onChange={setPatientId} />
        </Field>
        <Field label="Component">
          <Select value={component} onChange={(e) => setComponent(e.target.value)}>
            <option value="">Any component</option>
            {['whole_blood', 'prbc', 'ffp', 'platelets', 'cryoprecipitate'].map((c) => (
              <option key={c} value={c}>
                {componentLabel(c)}
              </option>
            ))}
          </Select>
        </Field>
      </FormSection>

      <FormSection title={tr('lab', 'unit')} icon={Droplet} columns={1}>
        {!patientId && <p className="text-sm text-muted-foreground">Choose a patient to see the compatible units.</p>}

        {compatible.isError && <Alert variant="warning">{describeError(compatible.error)} — group the patient before selecting a unit.</Alert>}

        {compatible.data && (
          <Field label="Unit" required hint={compatible.data.length === 0 ? 'No compatible unit is in stock' : 'Oldest compatible unit first'}>
            <Select value={bloodUnitId} onChange={(e) => setBloodUnitId(e.target.value)}>
              <option value="">Select…</option>
              {compatible.data.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.unitNo} · {GROUP_LABEL(unit.bloodGroup)} {componentLabel(unit.component)} · expires {dateOnly(unit.expiryDate)}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="Request" loading={request.isPending} incomplete={!valid} cancelTo={returnTo} />
    </FormScreen>
  );
}
